<script setup lang="ts">
import DateTimePickerField from '@/components/Fields/DateTimePickerField.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import TableColumn from '@/components/Table/TableColumn.vue'
import TitleTypography from '@/components/Typography/TitleTypography.vue'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import store from '@/store'
import { EDetectionActions } from '@/store/detectionStore/DetectionStoreTypes'
import type { EventCheckpointType } from '@/types/filters/EventCheckpointType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { DetectionModel } from '@/types/models/DetectionModel'
import type { EventModel } from '@/types/models/EventModel'
import { EPOIPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import { ESitesPerm } from '@/types/permissions/EventsPermissionsEnum'
import { durationCalc, formatTimestamp } from '@/utils/dateUtils'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DetectionsDesktopFilters from '../DetectionsDesktopFilters.vue'
import DetectionsTableView from '../DetectionsTableView.vue'
import EPCFilter from '../Filters/EPCFilter.vue'
import TripsExportComponent from './components/TripsExportComponent.vue'

const { t } = useI18n()

const permissions = computed(() => store.state.authentication?.me?.permissions ?? [])
const userHasSitePermission = computed(
    () => permissions.value?.find(item => item === ESitesPerm.EVENTS_VIEW)
)
const userHasPoiPermission = computed(
    () => permissions.value?.find(item => item === EPOIPerm.CHECKPOINTS_VIEW)
)

const columns = computed(() => ({
    origin_name: { field: 'origin_name', label: t('detection.table.origin_name') },
    destination_name: { field: 'destination_name', label: t('detection.table.destination_name') },
    epc: { field: 'epc', label: t('detection.table.epc') },
    start_at: { field: 'start_at', label: t('detection.table.arrived_at_origin') },
    end_at: { field: 'end_at', label: t('detection.table.arrived_at_destination') },
    duration: { field: 'duration', label: t('detection.table.duration') }
}))

const selectedOriginSite = ref<EventModel | undefined>()
const selectedDestinationSite = ref<EventModel | undefined>()

const selectedOriginPoi = ref<Partial<CheckpointModel> | undefined>()
const selectedDestinationPoi = ref<Partial<CheckpointModel> | undefined>()

const selectedArrivedAtFrom = ref<Date | undefined>()
const selectedArrivedAtTo = ref<Date | undefined>()
const selectedLeftAtFrom = ref<Date | undefined>()
const selectedLeftAtTo = ref<Date | undefined>()

const selectedEpc = ref<string | undefined>()

const filters = computed<any>(() => ({
    origin_site_id: selectedOriginSite.value?.id,
    origin_poi_id: selectedOriginPoi.value?.id,
    destination_site_id: selectedDestinationSite.value?.id,
    destination_poi_id: selectedDestinationPoi.value?.id,
    start_at_from: selectedArrivedAtFrom.value,
    start_at_to: selectedArrivedAtTo.value,
    end_at_from: selectedLeftAtFrom.value,
    end_at_to: selectedLeftAtTo.value,
    epc: selectedEpc.value
}))

const exportFilters = ref<{ origin: EventCheckpointType; destination: EventCheckpointType }>({
    origin: { event: selectedOriginSite.value, checkpoint: selectedOriginPoi.value },
    destination: { event: selectedDestinationSite.value, checkpoint: selectedDestinationPoi.value }
})
watch([selectedOriginSite, selectedOriginPoi], ([newOriginSite, newOriginPoi]) => {
    exportFilters.value.origin.event = newOriginSite
    exportFilters.value.origin.checkpoint = newOriginPoi
})
watch(
    [selectedDestinationSite, selectedDestinationPoi],
    ([newDestinationSite, newDestinationPoi]) => {
        exportFilters.value.destination.event = newDestinationSite
        exportFilters.value.destination.checkpoint = newDestinationPoi
    }
)

const handleRowClick = (row: DetectionModel) => {
    selectedOriginPoi.value = { id: row.origin_poi, name: row.origin_name }
    selectedDestinationPoi.value = { id: row.destination_poi, name: row.destination_name }
}

const exportFileNameData = computed(() => ({
    origin_site_name: exportFilters.value?.origin.event?.name,
    origin_poi_name: exportFilters.value?.origin.checkpoint?.name,

    destination_site_name: exportFilters.value?.destination.event?.name,
    destination_poi_name: exportFilters.value?.destination.checkpoint?.name
}))

const exportFiltersParsed = computed(() => ({
    origin_site_id: exportFilters.value?.origin?.event?.id,
    origin_poi_id: exportFilters.value?.origin?.checkpoint?.id,
    destination_site_id: exportFilters.value?.destination?.event?.id,
    destination_poi_id: exportFilters.value?.destination?.checkpoint?.id
}))
</script>

<template>
    <DetectionsTableView
        :storeAction="EDetectionActions.FETCH_PERSONS_TRIPS"
        :storeExportAction="EDetectionActions.EXPORT_PERSONS_TRIPS"
        :filters="filters"
        :exportFilters="exportFiltersParsed"
        :exportFileNameData="exportFileNameData"
        :disabledExport="
            !exportFileNameData.origin_site_name && !exportFileNameData.destination_site_name
        "
        @click="handleRowClick">
        <template #exportFilters>
            <TripsExportComponent
                :origin="exportFilters.origin"
                :destination="exportFilters.destination"
                @change="exportFilters = { ...exportFilters, ...$event }" />
        </template>
        <template #filters>
            <StackLayout direction="column" :gap="8">
                <EPCFilter :epc="filters.epc" @change="selectedEpc = $event" />
                <StackLayout
                    v-if="userHasSitePermission || userHasPoiPermission"
                    :gap="8"
                    class="trip-filters"
                    isResponsiveSmallScreen>
                    <TitleTypography :subtitle="$t('detection.filters.origin')" />
                    <DetectionsDesktopFilters
                        :filters="filters"
                        :selectedEvent="selectedOriginSite"
                        :selectedCheckpoint="selectedOriginPoi"
                        @selectEvent="selectedOriginSite = $event"
                        @selectCheckpoint="selectedOriginPoi = $event" />
                </StackLayout>
                <StackLayout
                    v-if="userHasSitePermission || userHasPoiPermission"
                    :gap="8"
                    class="trip-filters"
                    isResponsiveSmallScreen>
                    <TitleTypography :subtitle="$t('detection.filters.destination')" />
                    <DetectionsDesktopFilters
                        :filters="filters"
                        :selectedEvent="selectedDestinationSite"
                        :selectedCheckpoint="selectedDestinationPoi"
                        @selectEvent="selectedDestinationSite = $event"
                        @selectCheckpoint="selectedDestinationPoi = $event" />
                </StackLayout>

                <StackLayout :gap="8" isResponsiveSmallScreen>
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.arrived_at_from')"
                        :modelValue="filters.start_at_from"
                        @change="selectedArrivedAtFrom = $event"
                        class="fit-content" />
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.arrived_at_to')"
                        :modelValue="filters.start_at_to"
                        @change="selectedArrivedAtTo = $event"
                        class="fit-content" />
                </StackLayout>
                <StackLayout :gap="8" isResponsiveSmallScreen>
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.left_at_from')"
                        :modelValue="filters.end_at_from"
                        @change="selectedLeftAtFrom = $event"
                        class="fit-content" />
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.left_at_to')"
                        :modelValue="filters.end_at_to"
                        @change="selectedLeftAtTo = $event"
                        class="fit-content" />
                </StackLayout>
            </StackLayout>
        </template>
        <template #columns>
            <TableColumn
                :field="columns['origin_name'].field"
                :label="columns['origin_name'].label" />
            <TableColumn
                :field="columns['destination_name'].field"
                :label="columns['destination_name'].label" />
            <TableColumn :field="columns['epc'].field" :label="columns['epc'].label" />
            <TableColumn
                :field="columns['start_at'].field"
                :label="columns['start_at'].label"
                v-slot="props">
                <TypographyItem
                    v-if="props.row.start_at"
                    :label="formatTimestamp(props.row.start_at)" />
            </TableColumn>
            <TableColumn
                :field="columns['end_at'].field"
                :label="columns['end_at'].label"
                v-slot="props">
                <TypographyItem
                    v-if="props.row.end_at"
                    :label="formatTimestamp(props.row.end_at)" />
            </TableColumn>
            <TableColumn
                :field="columns['duration'].field"
                :label="columns['duration'].label"
                v-slot="props">
                <TypographyItem
                    v-if="props.row.start_at && props.row.end_at"
                    :label="durationCalc(props.row.end_at, props.row.start_at)" />
            </TableColumn>
        </template>
    </DetectionsTableView>
</template>

<style scoped lang="scss">
.trip-filters {
    white-space: nowrap;
    align-items: center;
}
</style>
